import type { NationId } from "@cwb/core";

/**
 * A state's banner, cut from one sprite sheet.
 *
 * It replaces the coloured square that used to stand in front of a nation's
 * name. The square carried exactly one bit — which colour this state draws in on
 * the map — and every panel that showed one was asking the player to hold seven
 * colours in their head. A banner carries the colour AND a device, so a state is
 * recognisable before its name is read.
 *
 * One sheet rather than seven files: seven requests for seven small images, all
 * needed on the same frame, and a sprite offset costs nothing to compute.
 *
 * The art is `apps/web/public/nation-flags.png`, sliced from the reference sheet
 * by `tools/slice-nation-flags.mjs` — cells are uniform and in `SHEET_ORDER`, so
 * replacing the art means replacing that file and the two cell constants.
 */

/** Left to right on the sheet. Exported so a test can hold it to the file. */
export const SHEET_ORDER: readonly NationId[] = ["qin", "zhao", "wei", "han", "chu", "qi", "yan"];
export const CELL_W = 165;
export const CELL_H = 168;

export interface NationFlagProps {
  nationId: NationId;
  /** Rendered height in px; the width follows the art. */
  height: number;
  /** Their colour, for the fallback block if the sheet ever loses a nation. */
  color?: string;
}

export function NationFlag({ nationId, height, color }: NationFlagProps) {
  const index = SHEET_ORDER.indexOf(nationId);
  const width = Math.round((CELL_W / CELL_H) * height);

  // A state the sheet has never heard of still has to render as something, and
  // a coloured block is what this replaced — so that is what it falls back to.
  if (index < 0) {
    return (
      <span
        className="nation-flag nation-flag--plain"
        style={{ width, height, background: color ?? "var(--dim)" }}
        aria-hidden="true"
      />
    );
  }

  const scale = height / CELL_H;
  return (
    <span
      className="nation-flag"
      style={{
        width,
        height,
        backgroundSize: `${CELL_W * SHEET_ORDER.length * scale}px ${CELL_H * scale}px`,
        backgroundPosition: `${-index * CELL_W * scale}px 0`,
      }}
      aria-hidden="true"
    />
  );
}
